import React, { useState, useEffect, useRef, useMemo } from 'react'
import { getSocket } from '../../features/midlleWare/socketMiddleware'

/**
 * Расчет времени печати
 * @param {number} width - Ширина макета в мм
 * @param {number} height - Длина (высота) макета в мм
 * @param {string} machineType - Тип машины ('x1' или 'x2')
 * @returns {number} - Время печати в секундах
 */
export function calculatePrintTime(width, height, machineType) {
  const config = {
    'x1': {
      step: 5.86,
      speed: 0.98,
      reverse: 0.6
    },
    'x2': {
      step: 7.5,
      speed: 0.93,
      reverse: 0.2
    }
  }

  const m = config[machineType] || config['x1']
  const passes = height / m.step
  const timePerPass = (width / 1000) / m.speed + m.reverse
  const totalSeconds = passes * timePerPass

  return Math.round(totalSeconds)
}

/**
 * Определение типа машины по названию ткани
 * @param {string} fabric - Название ткани
 * @returns {string} - Тип машины ('x1' или 'x2')
 */
export function getMachineType(fabric) {
  if (!fabric) return 'x1'
  
  const match = fabric.match(/(\d+)/)
  
  if (match) {
    const number = parseInt(match[1], 10)
    
    if (number === 320 || number === 260) {
      return 'x1'
    }
    
    if (number === 120 || number === 160 || number === 200 || number === 115) {
      return 'x2'
    }
  }
  
  return 'x1'
}

/**
 * Извлечение размеров и количества из имени файла
 * @param {string} fileName - Имя файла
 * @returns {Object|null} - {width, height, quantity} или null
 */
export function extractDimensions(fileName) {
  if (!fileName) return null
  
  const match = fileName.match(/(\d+)x(\d+)cm\s+x(\d+)/i)
  
  if (match) {
    return {
      width: parseInt(match[1], 10) * 10,
      height: parseInt(match[2], 10) * 10,
      quantity: parseInt(match[3], 10)
    }
  }
  
  const simpleMatch = fileName.match(/(\d+)x(\d+)cm/i)
  if (simpleMatch) {
    return {
      width: parseInt(simpleMatch[1], 10) * 10,
      height: parseInt(simpleMatch[2], 10) * 10,
      quantity: 1
    }
  }
  
  return null
}

/**
 * Расчет общего времени для всех элементов в папке ткани
 * @param {Object} rip - Объект с данными печати
 * @param {string} fabric - Название ткани
 * @returns {number} - Общее время в секундах
 */
function calculateTotalTime(rip, fabric) {
  if (!rip || !rip.item || rip.item.length === 0) return 0
  
  const machineType = getMachineType(fabric)
  let totalSeconds = 0
  
  rip.item.forEach(item => {
    const dimensions = extractDimensions(item.fileName)
    
    if (dimensions) {
      const timeForOne = calculatePrintTime(dimensions.width, dimensions.height, machineType)
      totalSeconds += timeForOne * dimensions.quantity
    } else {
      totalSeconds += calculatePrintTime(1520, 2550, machineType)
    }
  })
  
  return totalSeconds
}

/**
 * Форматирование времени
 * @param {number} totalSeconds - Время в секундах
 * @returns {string} - Форматированное время
 */
function formatTime(totalSeconds) {
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const secs = totalSeconds % 60

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
}

// export function FabricTimer({ fabric, rip, id = 'default', onRunningChange, readOnly = false }) {
//   const estimatedTime = useMemo(() => calculateTotalTime(rip, fabric), [rip, fabric])
//   const onRunningChangeRef = useRef(onRunningChange)
//   const storageKey = `fabric_timer_${id}_seconds`
//   const isPrintingKey = `fabric_timer_${id}_isPrinting`
// 
//   useEffect(() => {
//     onRunningChangeRef.current = onRunningChange
//   }, [onRunningChange])
// 
//   const [seconds, setSeconds] = useState(() => {
//     const saved = localStorage.getItem(storageKey)
//     if (saved !== null) {
//       const parsed = parseInt(saved, 10)
//       return parsed <= estimatedTime ? parsed : estimatedTime
//     }
//     return estimatedTime
//   })
// 
//   const [isPrinting, setIsPrinting] = useState(() => {
//     return localStorage.getItem(isPrintingKey) === 'true'
//   })
// 
//   useEffect(() => {
//     if (onRunningChangeRef.current) onRunningChangeRef.current(isPrinting)
//   }, [isPrinting])
// 
//   useEffect(() => {
//     const socket = getSocket()
// 
//     const handleSubtract = (e) => {
//       const data = e.detail
//       if (data.id !== id) return
//       setSeconds(prev => {
//         const next = Math.max(prev - data.seconds, 0)
//         localStorage.setItem(storageKey, next.toString())
//         return next
//       })
//     }
// 
//     const handlePrintingSync = (data) => {
//       if (data.id !== id) return
//       setIsPrinting(data.isPrinting)
//       localStorage.setItem(isPrintingKey, data.isPrinting.toString())
//       if (onRunningChangeRef.current) onRunningChangeRef.current(data.isPrinting)
//     }
// 
//     const handleSetPrinting = (e) => {
//       const data = e.detail
//       if (data.id !== id) return
//       setIsPrinting(data.isPrinting)
//       localStorage.setItem(isPrintingKey, data.isPrinting.toString())
//       if (onRunningChangeRef.current) onRunningChangeRef.current(data.isPrinting)
//     }
// 
//     window.addEventListener('timer_subtract', handleSubtract)
//     window.addEventListener('timer_set_printing', handleSetPrinting)
// 
//     if (socket) {
//       socket.on('timer_printing', handlePrintingSync)
//     }
// 
//     return () => {
//       window.removeEventListener('timer_subtract', handleSubtract)
//       window.removeEventListener('timer_set_printing', handleSetPrinting)
//       if (socket) socket.off('timer_printing', handlePrintingSync)
//     }
//   }, [id, storageKey, isPrintingKey])
// 
//   const handleClick = () => {
//     if (readOnly) return
//     const next = !isPrinting
//     setIsPrinting(next)
//     localStorage.setItem(isPrintingKey, next.toString())
//     const socket = getSocket()
//     if (socket && socket.connected) {
//       socket.emit('timer_printing', { id, isPrinting: next })
//     }
//   }
// 
//   const progress = estimatedTime > 0 ? Math.min(((estimatedTime - seconds) / estimatedTime) * 100, 100) : 0
//   const itemCount = rip?.item?.length || 0
// 
//   return (
//     <div
//       onClick={handleClick}
//       style={{
//         display: 'flex', flexDirection: 'column', alignItems: 'center',
//         justifyContent: 'center', width: '100%', height: '100%',
//         cursor: readOnly ? 'default' : 'pointer', userSelect: 'none'
//       }}
//     >
//       <div style={{ fontSize: '14px', fontWeight: 'bold', color: 'white' }}>
//         {formatTime(seconds)}
//       </div>
//       <div style={{ width: '90%', height: '3px', backgroundColor: 'rgba(255,255,255,0.3)', borderRadius: '2px', overflow: 'hidden', margin: '2px 0' }}>
//         <div style={{ width: `${progress}%`, height: '100%', backgroundColor: '#FFA726', transition: 'width 0.3s ease' }} />
//       </div>
//       <div style={{ fontSize: '8px', color: 'rgba(255,255,255,0.7)', marginTop: '1px' }}>
//         {`${itemCount} pcs`}
//       </div>
//     </div>
//   )
// }
